import { useCallback, useMemo, useState } from "react";
import { CloseTemplateBasis } from "@common/types/closeTemplateBasis";
import { OrganizationCloseStage } from "@common/types/organizationCloseStage";
import { View } from "@common/types/views/views";
import {
    WORKSPACE_USER_CACHE_KEYS,
    useWorkspaceUserCache,
} from "@hooks/useWorkspaceUserCache";
import { ViewTableMeta } from "@hooks/view/useViewTableMeta";
import { SubScopeProvider } from "@state/subScopeContext";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { AddCloseTemplateTaskDrawer } from "./AddCloseTemplateTaskDrawer";
import { EditCloseTemplateTaskDrawer } from "./EditCloseTemplateTaskDrawer";
import { CloseTemplateTasksTableFooter } from "./CloseTemplateTasksTableFooter";
import { CloseTemplateTasksEmptyView } from "./CloseTemplateTasksEmptyView";
import {
    closeTemplateTasksViewConfig,
    CloseTemplateTaskCallbacks,
    CloseTemplateTaskMeta,
} from "./closeTemplateTasksViewConfig";
import { useCloseTemplateTaskBulkActions } from "./useCloseTemplateTaskBulkActions";
import { useCloseTemplateTasksDragAndDrop } from "./useCloseTemplateTasksDragAndDrop";
import { useCloseTemplateTaskSelection } from "./useCloseTemplateTaskSelection";
import { useCloseTemplateTaskCallbacks } from "./useCloseTemplateTaskCallbacks";
import {
    CloseTemplateTasksViewSchema,
    defaultView,
} from "./closeTemplateTasksDrawer.constants";
import { LocalTask } from "./closeTemplateTasksDrawer.types";
import { useOrgTaskTemplatesForWorkspace } from "./useOrgTaskTemplatesForWorkspace";
import { useOutdatedTemplateTaskIds } from "./useOutdatedTemplateTaskIds";
import styles from "./CloseTemplateTasksDrawer.module.scss";

interface Props {
    localTasks: LocalTask[];
    onLocalTasksChange: (updater: (prev: LocalTask[]) => LocalTask[]) => void;
    stages: OrganizationCloseStage[];
    isSaving: boolean;
    isDirty: boolean;
    onSave: () => void;
    onSaveAndApply: () => void;
    onApply: () => void;
    basis: CloseTemplateBasis | null;
    /** The template's client workspace; null/undefined for org-wide templates. */
    workspaceId?: string | null;
}

/**
 * Mounted once per closeTemplateId (parent passes key={closeTemplateId}).
 * Receives pre-fetched data as props — no data fetching or initialisation
 * effects required.
 *
 * Scopes everything inside (incl. its own hooks, via the Inner split) to the
 * template's client workspace so descendants like the transactions preview and
 * the review-fields selector can read it from useScope().
 */
export const CloseTemplateTasksDrawerContent = (props: Props) => (
    <SubScopeProvider value={{ workspaceId: props.workspaceId ?? undefined }}>
        <CloseTemplateTasksDrawerContentInner {...props} />
    </SubScopeProvider>
);

const CloseTemplateTasksDrawerContentInner = ({
    localTasks,
    onLocalTasksChange,
    stages,
    isSaving,
    isDirty,
    onSave,
    onSaveAndApply,
    onApply,
    basis,
    workspaceId,
}: Props) => {
    const orgTaskTemplates = useOrgTaskTemplatesForWorkspace({
        workspaceId: workspaceId ?? null,
        basis,
    });
    const outdatedLocalIds = useOutdatedTemplateTaskIds(localTasks);
    const [cachedView, setCachedView] = useWorkspaceUserCache<View>(
        WORKSPACE_USER_CACHE_KEYS.CLOSE_TEMPLATE_TASKS_DRAWER_VIEW_CACHE_KEY,
        CloseTemplateTasksViewSchema,
    );
    const [view, setView] = useState<View>(cachedView ?? defaultView);
    const sortedStages = useMemo(
        () => [...stages].sort((a, b) => a.order - b.order),
        [stages],
    );

    const knownStageIds = useMemo(
        () => new Set(sortedStages.map((stage) => stage.id)),
        [sortedStages],
    );

    const activeTaskCount = useMemo(
        () => localTasks.filter((t) => !t.isDeleted).length,
        [localTasks],
    );

    const rowDragAndDropConfig = useCloseTemplateTasksDragAndDrop({
        onLocalTasksChange,
        search: "",
    });

    const {
        tableRows,
        addTaskStageId,
        isAddTaskDrawerOpen,
        editingTask,
        hiddenStages,
        whitelistedStageIds,
        activeTaskCountByStageKey,
        handleAddStageToWhitelist,
        handleRemoveStageFromWhitelist,
        handleRemoveTask,
        handleEditTask,
        handleSaveEditedTask,
        handleUpdateAssigneeRole,
        handleUpdateCloseStage,
        handleUpdateDeadline,
        handleUpdateDeadlineAnchor,
        handleUpdateEstimatedTime,
        handleOpenAddTaskDrawer,
        handleAddTaskForStage,
        handleAddTaskFromDrawer,
        handleCloseAddTaskDrawer,
        handleCloseEditTaskDrawer,
    } = useCloseTemplateTaskCallbacks({
        localTasks,
        onLocalTasksChange,
        sortedStages,
        knownStageIds,
        search: "",
    });

    const editingLatestSourceTemplate = useMemo(
        () =>
            editingTask && outdatedLocalIds.has(editingTask.localId)
                ? (orgTaskTemplates.find(
                      (template) =>
                          template.id === editingTask.sourceTemplateId,
                  ) ?? null)
                : null,
        [editingTask, outdatedLocalIds, orgTaskTemplates],
    );

    const {
        rowSelection,
        setRowSelection,
        clearSelection,
        canSelectRow,
        selectedLocalIds,
        selectedLocalIdsSet,
        handleBulkSelectStage,
        taskIdsByStageKey,
    } = useCloseTemplateTaskSelection({
        localTasks,
        tableRows,
    });

    const { handleOpenBulkActions } = useCloseTemplateTaskBulkActions({
        selectedLocalIds,
        localTasks,
        onLocalTasksChange,
        clearSelection,
        sortedStages,
    });

    const tableMeta = useMemo<
        ViewTableMeta<CloseTemplateTaskCallbacks, never> & CloseTemplateTaskMeta
    >(
        () => ({
            callbacks: {
                onRemoveTask: handleRemoveTask,
                onEditTask: handleEditTask,
                onUpdateAssigneeRole: handleUpdateAssigneeRole,
                onUpdateCloseStage: handleUpdateCloseStage,
                onUpdateDeadline: handleUpdateDeadline,
                onUpdateDeadlineAnchor: handleUpdateDeadlineAnchor,
                onUpdateEstimatedTime: handleUpdateEstimatedTime,
                onAddTaskForStage: handleAddTaskForStage,
                onBulkSelectStage: handleBulkSelectStage,
                onRemoveStageFromWhitelist: handleRemoveStageFromWhitelist,
            },
            sortedStages,
            selectedLocalIds: selectedLocalIdsSet,
            taskIdsByStageKey,
            activeTaskCountByStageKey,
            whitelistedStageIds,
            outdatedLocalIds,
        }),
        [
            handleRemoveTask,
            handleEditTask,
            handleUpdateAssigneeRole,
            handleUpdateCloseStage,
            handleUpdateDeadline,
            handleUpdateDeadlineAnchor,
            handleUpdateEstimatedTime,
            handleAddTaskForStage,
            handleBulkSelectStage,
            handleRemoveStageFromWhitelist,
            sortedStages,
            selectedLocalIdsSet,
            taskIdsByStageKey,
            activeTaskCountByStageKey,
            whitelistedStageIds,
            outdatedLocalIds,
        ],
    );

    const handleViewChange = useCallback(
        (newView: View) => {
            const selectableView = {
                ...newView,
                options: {
                    ...newView.options,
                    selection: true,
                },
            };
            setView(selectableView);
            setCachedView(selectableView);
        },
        [setCachedView],
    );

    const selectableView = useMemo(
        () => ({
            ...view,
            options: {
                ...view.options,
                selection: true,
            },
        }),
        [view],
    );

    return (
        <>
            <div className={styles.drawerContent}>
                <ViewTable
                    data={tableRows}
                    view={selectableView}
                    config={closeTemplateTasksViewConfig}
                    emptyView={() => (
                        <CloseTemplateTasksEmptyView
                            hiddenStages={hiddenStages}
                            onAddStage={handleAddStageToWhitelist}
                            onAddTask={handleOpenAddTaskDrawer}
                        />
                    )}
                    className={styles.tasksTable}
                    tableMeta={tableMeta}
                    enableColumnResizing={false}
                    onViewChange={handleViewChange}
                    autoExpandOnMount
                    fixedTopOffsetPx
                    rowDragAndDrop={rowDragAndDropConfig}
                    rowSelection={rowSelection}
                    onRowSelectionChange={setRowSelection}
                    enableRowSelection={canSelectRow}
                    footer={
                        <>
                            <CloseTemplateTasksTableFooter
                                isSaving={isSaving}
                                isDirty={isDirty}
                                onSave={onSave}
                                onSaveAndApply={onSaveAndApply}
                                onApply={onApply}
                                onAddTask={handleOpenAddTaskDrawer}
                                onAddStage={handleAddStageToWhitelist}
                                hiddenStages={hiddenStages}
                                taskCount={activeTaskCount}
                                selectedCount={selectedLocalIds.length}
                                onDeselectRows={clearSelection}
                                onOpenBulkActions={handleOpenBulkActions}
                            />
                        </>
                    }
                />
            </div>

            <AddCloseTemplateTaskDrawer
                open={isAddTaskDrawerOpen}
                initialCloseStageId={addTaskStageId}
                onClose={handleCloseAddTaskDrawer}
                onAddTask={handleAddTaskFromDrawer}
                sortedStages={sortedStages}
                basis={basis}
                orgTaskTemplates={orgTaskTemplates}
                linkableTasks={localTasks}
            />

            <EditCloseTemplateTaskDrawer
                key={editingTask?.localId ?? "none"}
                open={editingTask !== null}
                task={editingTask}
                sortedStages={sortedStages}
                onClose={handleCloseEditTaskDrawer}
                onSave={handleSaveEditedTask}
                linkableTasks={localTasks}
                basis={basis}
                latestSourceTemplate={editingLatestSourceTemplate}
            />
        </>
    );
};
